import React, { useEffect, useState, useRef } from 'react';
import { slaughterApi } from '../services/api';
import { Slaughter } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { handleApiError, showError, showSuccess } from '../utils/handleApiError';
import AnimalSelectorModal, { SelectedAnimal } from '../components/AnimalSelectorModal';
import ClearableInput from '../components/ClearableInput';
import Pagination from '../components/Pagination';

const Slaughters: React.FC = () => {
  const { t } = useLanguage();
  const [slaughters, setSlaughters] = useState<Slaughter[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAnimalSelector, setShowAnimalSelector] = useState(false);
  const [addAnimalDisplay, setAddAnimalDisplay] = useState('');
  const [editAnimalDisplay, setEditAnimalDisplay] = useState('');
  const [editingSlaughter, setEditingSlaughter] = useState<Slaughter | null>(null);
  const [deletingSlaughter, setDeletingSlaughter] = useState<Slaughter | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [newSlaughter, setNewSlaughter] = useState<Partial<Slaughter>>({
    animal_id: 0,
    slaughter_date: new Date().toISOString().split('T')[0],
    slaughterhouse: '',
    carcass_weight: 0,
    meat_classification: '',
    fat_classification: '',
    price_per_kg: 0,
    total_price: 0,
    inspector: '',
    certificate_number: '',
    invoice_number: '',
    notes: '',
  });

  // Filters
  const [filterEarTag, setFilterEarTag] = useState('');
  const [filterSlaughterhouse, setFilterSlaughterhouse] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Debounced text filters
  const [debouncedEarTag, setDebouncedEarTag] = useState('');
  const [debouncedSlaughterhouse, setDebouncedSlaughterhouse] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedEarTag(filterEarTag), 300);
    return () => clearTimeout(timer);
  }, [filterEarTag]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSlaughterhouse(filterSlaughterhouse), 300);
    return () => clearTimeout(timer);
  }, [filterSlaughterhouse]);

  const prevDeps = useRef({ debouncedEarTag, debouncedSlaughterhouse, filterDateFrom, filterDateTo });
  const isInitialMount = useRef(true);

  useEffect(() => {
    const filtersChanged = !isInitialMount.current && (
      prevDeps.current.debouncedEarTag !== debouncedEarTag ||
      prevDeps.current.debouncedSlaughterhouse !== debouncedSlaughterhouse ||
      prevDeps.current.filterDateFrom !== filterDateFrom ||
      prevDeps.current.filterDateTo !== filterDateTo
    );
    prevDeps.current = { debouncedEarTag, debouncedSlaughterhouse, filterDateFrom, filterDateTo };
    isInitialMount.current = false;

    if (filtersChanged && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }

    loadSlaughters(currentPage);
  }, [currentPage, debouncedEarTag, debouncedSlaughterhouse, filterDateFrom, filterDateTo]);

  const loadSlaughters = async (page: number) => {
    try {
      setLoading(true);
      const params: Record<string, any> = { page };
      if (debouncedEarTag) params.ear_tag = debouncedEarTag;
      if (debouncedSlaughterhouse) params.slaughterhouse = debouncedSlaughterhouse;
      if (filterDateFrom) params.from_date = filterDateFrom;
      if (filterDateTo) params.to_date = filterDateTo;
      const response = await slaughterApi.getAll(params);
      setSlaughters(response.data);
      setCurrentPage(response.current_page);
      setLastPage(response.last_page);
      setTotal(response.total);
    } catch (error) {
      console.error('Error loading slaughters:', error);
      handleApiError(error, t('error_loading_slaughters', 'slaughters') || 'Error loading slaughters');
    } finally {
      setLoading(false);
    }
  };

  const handleAnimalSelected = (animals: SelectedAnimal[]) => {
    const animal = animals[0];
    const display = `${animal.ear_tag}${animal.breed ? ` - ${animal.breed}` : ''}`;

    if (showAddModal) {
      setNewSlaughter(prev => ({ ...prev, animal_id: animal.id }));
      setAddAnimalDisplay(display);
    } else if (showEditModal && editingSlaughter) {
      setEditingSlaughter(prev => prev ? ({ ...prev, animal_id: animal.id }) : null);
      setEditAnimalDisplay(display);
    }
    setShowAnimalSelector(false);
  };

  const handleAddSlaughter = async () => {
    try {
      if (!newSlaughter.animal_id || !newSlaughter.slaughter_date) {
        showError(t('fill_required_fields', 'slaughters') || 'Please fill in all required fields');
        return;
      }

      await slaughterApi.create(newSlaughter);
      showSuccess(t('slaughter_added_successfully', 'slaughters') || 'Slaughter record added successfully');
      setShowAddModal(false);
      resetNewSlaughter();
      loadSlaughters(currentPage);
    } catch (error) {
      console.error('Error creating slaughter:', error);
      handleApiError(error, t('error_creating_slaughter', 'slaughters') || 'Error creating slaughter record');
    }
  };

  const handleEditSlaughter = async () => {
    try {
      if (!editingSlaughter || !editingSlaughter.animal_id || !editingSlaughter.slaughter_date) {
        showError(t('fill_required_fields', 'slaughters') || 'Please fill in all required fields');
        return;
      }

      await slaughterApi.update(editingSlaughter.id, editingSlaughter);
      showSuccess(t('slaughter_updated_successfully', 'slaughters') || 'Slaughter record updated successfully');
      setShowEditModal(false);
      setEditingSlaughter(null);
      loadSlaughters(currentPage);
    } catch (error) {
      console.error('Error updating slaughter:', error);
      handleApiError(error, t('error_updating_slaughter', 'slaughters') || 'Error updating slaughter record');
    }
  };

  const handleDeleteSlaughter = async () => {
    try {
      if (!deletingSlaughter) return;

      await slaughterApi.delete(deletingSlaughter.id);
      showSuccess(t('slaughter_deleted_successfully', 'slaughters') || 'Slaughter record deleted successfully');
      setShowDeleteModal(false);
      setDeletingSlaughter(null);
      loadSlaughters(currentPage);
    } catch (error) {
      console.error('Error deleting slaughter:', error);
      handleApiError(error, t('error_deleting_slaughter', 'slaughters') || 'Error deleting slaughter record');
    }
  };

  const resetNewSlaughter = () => {
    setNewSlaughter({
      animal_id: 0,
      slaughter_date: new Date().toISOString().split('T')[0],
      slaughterhouse: '',
      carcass_weight: 0,
      meat_classification: '',
      fat_classification: '',
      price_per_kg: 0,
      total_price: 0,
      inspector: '',
      certificate_number: '',
      invoice_number: '',
      notes: '',
    });
    setAddAnimalDisplay('');
  };

  const openEditModal = (slaughter: Slaughter) => {
    setEditingSlaughter({ ...slaughter });
    setEditAnimalDisplay(
      slaughter.animal ? `${slaughter.animal.ear_tag}${slaughter.animal.breed ? ` - ${slaughter.animal.breed}` : ''}` : ''
    );
    setShowEditModal(true);
  };

  const openDeleteModal = (slaughter: Slaughter) => {
    setDeletingSlaughter(slaughter);
    setShowDeleteModal(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('hu-HU');
  };

  const formatCurrency = (amount: number | undefined) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
    }).format(amount);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-128px)]">
      <div className="mb-4 flex-shrink-0">
        <h1 className="text-3xl font-bold text-gray-900">{t('slaughters_management', 'slaughters') || 'Slaughters Management'}</h1>
        <p className="mt-2 text-gray-600">
          {t('slaughters_management_desc', 'slaughters') || 'Manage animal slaughter records'}
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4 mb-4 flex-shrink-0">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('filters', 'common') || 'Filters'}</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('ear_tag', 'animal') || 'Ear Tag'}
            </label>
            <ClearableInput
              value={filterEarTag}
              onChange={(val) => setFilterEarTag(val)}
              placeholder={t('search_by_ear_tag', 'animal') || 'Search by ear tag'}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('slaughterhouse', 'slaughters') || 'Slaughterhouse'}
            </label>
            <ClearableInput
              value={filterSlaughterhouse}
              onChange={(val) => setFilterSlaughterhouse(val)}
              placeholder={t('search_by_slaughterhouse', 'slaughters') || 'Search by slaughterhouse'}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('date_from', 'common') || 'Date From'}
            </label>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('date_to', 'common') || 'Date To'}
            </label>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Slaughters Table */}
      <div className="bg-white shadow rounded-lg flex flex-col flex-1 min-h-0">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {t('slaughter_records', 'slaughters') || 'Slaughter Records'} ({total})
          </h3>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span>{t('add', 'action') || 'Add'}</span>
          </button>
        </div>

        <div className="overflow-auto flex-1 min-h-0 relative">
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-60 z-10 flex items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
          )}
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-[5]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('slaughter_date', 'slaughters') || 'Slaughter Date'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('animal', 'slaughters') || 'Animal'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('slaughterhouse', 'slaughters') || 'Slaughterhouse'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('carcass_weight', 'slaughters') || 'Carcass Weight'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('meat_classification', 'slaughters') || 'Meat Class'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('total_price', 'slaughters') || 'Total Price'}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('actions', 'common') || 'Actions'}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {slaughters.map((slaughter) => (
                <tr key={slaughter.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {formatDate(slaughter.slaughter_date)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {slaughter.animal?.ear_tag || '-'}
                    </div>
                    {slaughter.animal?.breed && (
                      <div className="text-xs text-gray-500">{slaughter.animal.breed}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{slaughter.slaughterhouse || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {slaughter.carcass_weight ? `${slaughter.carcass_weight} kg` : '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {slaughter.meat_classification || '-'}
                      {slaughter.fat_classification && ` / ${slaughter.fat_classification}`}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {formatCurrency(slaughter.total_price)}
                    </div>
                    {slaughter.price_per_kg && (
                      <div className="text-xs text-gray-500">
                        {formatCurrency(slaughter.price_per_kg)}/kg
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => openEditModal(slaughter)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      {t('edit', 'action') || 'Edit'}
                    </button>
                    <button
                      onClick={() => openDeleteModal(slaughter)}
                      className="text-red-600 hover:text-red-900"
                    >
                      {t('delete', 'action') || 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {slaughters.length === 0 && !loading && (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {t('no_slaughter_records', 'slaughters') || 'No slaughter records'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {t('no_slaughter_records_desc', 'slaughters') || 'Slaughter records will appear here'}
              </p>
            </div>
          )}
        </div>

        {lastPage > 1 && (
          <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 flex-shrink-0">
            <div className="text-sm text-gray-700">
              {((currentPage - 1) * 50) + 1}–{Math.min(currentPage * 50, total)} / {total}
            </div>
            <Pagination page={currentPage} lastPage={lastPage} onPageChange={setCurrentPage} disabled={loading} />
          </div>
        )}
      </div>

      {/* Add Slaughter Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white max-h-[calc(100vh-100px)] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">{t('add_slaughter_record', 'slaughters') || 'Add Slaughter Record'}</h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetNewSlaughter();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* Animal Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('animal', 'slaughters') || 'Animal'} *
                </label>
                <div className="relative">
                  <div
                    onClick={() => setShowAnimalSelector(true)}
                    className={`w-full px-3 py-2 border rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      newSlaughter.animal_id ? 'border-gray-300 text-gray-900' : 'border-gray-300 text-gray-400'
                    }`}
                  >
                    {addAnimalDisplay || `${t('select_animal', 'slaughters') || 'Select animal'}...`}
                  </div>
                  {newSlaughter.animal_id ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setNewSlaughter(prev => ({ ...prev, animal_id: 0 }));
                        setAddAnimalDisplay('');
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Slaughter Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('slaughter_date', 'slaughters') || 'Slaughter Date'} *
                  </label>
                  <input
                    type="date"
                    value={newSlaughter.slaughter_date || ''}
                    onChange={(e) => setNewSlaughter(prev => ({ ...prev, slaughter_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Slaughterhouse */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('slaughterhouse', 'slaughters') || 'Slaughterhouse'}
                  </label>
                  <input
                    type="text"
                    value={newSlaughter.slaughterhouse || ''}
                    onChange={(e) => setNewSlaughter(prev => ({ ...prev, slaughterhouse: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('slaughterhouse_placeholder', 'slaughters') || 'e.g., ABC Slaughterhouse'}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Carcass Weight */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('carcass_weight', 'slaughters') || 'Carcass Weight'} (kg)
                  </label>
                  <input
                    type="number"
                    value={newSlaughter.carcass_weight || ''}
                    onChange={(e) => setNewSlaughter(prev => ({ ...prev, carcass_weight: parseFloat(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    step="0.1"
                  />
                </div>

                {/* Price per kg */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('price_per_kg', 'slaughters') || 'Price per kg'} (HUF)
                  </label>
                  <input
                    type="number"
                    value={newSlaughter.price_per_kg || ''}
                    onChange={(e) => setNewSlaughter(prev => ({ ...prev, price_per_kg: parseFloat(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Meat Classification */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('meat_classification', 'slaughters') || 'Meat Classification'}
                  </label>
                  <input
                    type="text"
                    value={newSlaughter.meat_classification || ''}
                    onChange={(e) => setNewSlaughter(prev => ({ ...prev, meat_classification: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('meat_classification_placeholder', 'slaughters') || 'e.g., E, U, R, O, P'}
                  />
                </div>

                {/* Fat Classification */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('fat_classification', 'slaughters') || 'Fat Classification'}
                  </label>
                  <input
                    type="text"
                    value={newSlaughter.fat_classification || ''}
                    onChange={(e) => setNewSlaughter(prev => ({ ...prev, fat_classification: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('fat_classification_placeholder', 'slaughters') || 'e.g., 1, 2, 3, 4, 5'}
                  />
                </div>
              </div>

              {/* Total Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('total_price', 'slaughters') || 'Total Price'} (HUF)
                </label>
                <input
                  type="number"
                  value={newSlaughter.total_price || ''}
                  onChange={(e) => setNewSlaughter(prev => ({ ...prev, total_price: parseFloat(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Inspector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('inspector', 'slaughters') || 'Inspector'}
                  </label>
                  <input
                    type="text"
                    value={newSlaughter.inspector || ''}
                    onChange={(e) => setNewSlaughter(prev => ({ ...prev, inspector: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('inspector_placeholder', 'slaughters') || 'Inspector name'}
                  />
                </div>

                {/* Certificate Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('certificate_number', 'slaughters') || 'Certificate Number'}
                  </label>
                  <input
                    type="text"
                    value={newSlaughter.certificate_number || ''}
                    onChange={(e) => setNewSlaughter(prev => ({ ...prev, certificate_number: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('certificate_number_placeholder', 'slaughters') || 'Certificate number'}
                  />
                </div>
              </div>

              {/* Invoice Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoice_number', 'slaughters') || 'Invoice Number'}
                </label>
                <input
                  type="text"
                  value={newSlaughter.invoice_number || ''}
                  onChange={(e) => setNewSlaughter(prev => ({ ...prev, invoice_number: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('invoice_number_placeholder', 'slaughters') || 'Invoice number'}
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('notes', 'common') || 'Notes'}
                </label>
                <textarea
                  value={newSlaughter.notes || ''}
                  onChange={(e) => setNewSlaughter(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder={t('notes_placeholder', 'slaughters') || 'Additional information...'}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-8">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetNewSlaughter();
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {t('cancel', 'action') || 'Cancel'}
              </button>
              <button
                onClick={handleAddSlaughter}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {t('save', 'action') || 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Slaughter Modal */}
      {showEditModal && editingSlaughter && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white max-h-[calc(100vh-100px)] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">{t('edit_slaughter_record', 'slaughters') || 'Edit Slaughter Record'}</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingSlaughter(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* Animal Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('animal', 'slaughters') || 'Animal'} *
                </label>
                <div className="relative">
                  <div
                    onClick={() => setShowAnimalSelector(true)}
                    className={`w-full px-3 py-2 border rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      editingSlaughter.animal_id ? 'border-gray-300 text-gray-900' : 'border-gray-300 text-gray-400'
                    }`}
                  >
                    {editAnimalDisplay || `${t('select_animal', 'slaughters') || 'Select animal'}...`}
                  </div>
                  {editingSlaughter.animal_id ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingSlaughter(prev => prev ? ({ ...prev, animal_id: 0 }) : null);
                        setEditAnimalDisplay('');
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Slaughter Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('slaughter_date', 'slaughters') || 'Slaughter Date'} *
                  </label>
                  <input
                    type="date"
                    value={editingSlaughter.slaughter_date || ''}
                    onChange={(e) => setEditingSlaughter(prev => prev ? ({ ...prev, slaughter_date: e.target.value }) : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Slaughterhouse */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('slaughterhouse', 'slaughters') || 'Slaughterhouse'}
                  </label>
                  <input
                    type="text"
                    value={editingSlaughter.slaughterhouse || ''}
                    onChange={(e) => setEditingSlaughter(prev => prev ? ({ ...prev, slaughterhouse: e.target.value }) : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Carcass Weight */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('carcass_weight', 'slaughters') || 'Carcass Weight'} (kg)
                  </label>
                  <input
                    type="number"
                    value={editingSlaughter.carcass_weight || ''}
                    onChange={(e) => setEditingSlaughter(prev => prev ? ({ ...prev, carcass_weight: parseFloat(e.target.value) }) : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    step="0.1"
                  />
                </div>

                {/* Price per kg */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('price_per_kg', 'slaughters') || 'Price per kg'} (HUF)
                  </label>
                  <input
                    type="number"
                    value={editingSlaughter.price_per_kg || ''}
                    onChange={(e) => setEditingSlaughter(prev => prev ? ({ ...prev, price_per_kg: parseFloat(e.target.value) }) : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Meat Classification */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('meat_classification', 'slaughters') || 'Meat Classification'}
                  </label>
                  <input
                    type="text"
                    value={editingSlaughter.meat_classification || ''}
                    onChange={(e) => setEditingSlaughter(prev => prev ? ({ ...prev, meat_classification: e.target.value }) : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Fat Classification */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('fat_classification', 'slaughters') || 'Fat Classification'}
                  </label>
                  <input
                    type="text"
                    value={editingSlaughter.fat_classification || ''}
                    onChange={(e) => setEditingSlaughter(prev => prev ? ({ ...prev, fat_classification: e.target.value }) : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Total Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('total_price', 'slaughters') || 'Total Price'} (HUF)
                </label>
                <input
                  type="number"
                  value={editingSlaughter.total_price || ''}
                  onChange={(e) => setEditingSlaughter(prev => prev ? ({ ...prev, total_price: parseFloat(e.target.value) }) : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Inspector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('inspector', 'slaughters') || 'Inspector'}
                  </label>
                  <input
                    type="text"
                    value={editingSlaughter.inspector || ''}
                    onChange={(e) => setEditingSlaughter(prev => prev ? ({ ...prev, inspector: e.target.value }) : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Certificate Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('certificate_number', 'slaughters') || 'Certificate Number'}
                  </label>
                  <input
                    type="text"
                    value={editingSlaughter.certificate_number || ''}
                    onChange={(e) => setEditingSlaughter(prev => prev ? ({ ...prev, certificate_number: e.target.value }) : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Invoice Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoice_number', 'slaughters') || 'Invoice Number'}
                </label>
                <input
                  type="text"
                  value={editingSlaughter.invoice_number || ''}
                  onChange={(e) => setEditingSlaughter(prev => prev ? ({ ...prev, invoice_number: e.target.value }) : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('notes', 'common') || 'Notes'}
                </label>
                <textarea
                  value={editingSlaughter.notes || ''}
                  onChange={(e) => setEditingSlaughter(prev => prev ? ({ ...prev, notes: e.target.value }) : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-8">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingSlaughter(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {t('cancel', 'action') || 'Cancel'}
              </button>
              <button
                onClick={handleEditSlaughter}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {t('update', 'action') || 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Animal Selector Modal */}
      <AnimalSelectorModal
        isOpen={showAnimalSelector}
        onClose={() => setShowAnimalSelector(false)}
        onSelect={handleAnimalSelected}
        mode="single"
        title={t('select_animal', 'slaughters') || 'Select animal'}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingSlaughter && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg leading-6 font-medium text-gray-900 mt-5">
                {t('delete_slaughter_record', 'slaughters') || 'Delete Slaughter Record'}
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  {t('delete_slaughter_confirmation', 'slaughters') || 'Are you sure you want to delete this slaughter record? This action cannot be undone.'}
                </p>
                {deletingSlaughter.animal && (
                  <p className="text-sm text-gray-700 mt-2 font-medium">
                    {t('animal', 'slaughters')}: {deletingSlaughter.animal.ear_tag}
                  </p>
                )}
              </div>
              <div className="flex justify-center space-x-4 px-4 py-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletingSlaughter(null);
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  {t('cancel', 'action') || 'Cancel'}
                </button>
                <button
                  onClick={handleDeleteSlaughter}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  {t('delete', 'action') || 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Slaughters;
